import * as THREE from "three";
import { isSeriesParallelNodeId } from "../electrical";
import type { InteractionSystem } from "../xr/input";
import type { XRRig } from "../xr/session";
import type { SeriesParallelBenchRoots } from "../world/seriesParallelBench";
import { createGrabbable, type Grabbable } from "./grabbable";
import { GrabManager, type PlaceOutcome } from "./GrabManager";
import {
  createPlacementTarget,
  DEFAULT_WIRE_SNAP_RADIUS,
  isOccupiedBy,
  lockIntoTarget,
  type PlacementTarget,
} from "./placement";

/** Local Y of a jumper end when seated on a circuit terminal cap. */
const TERMINAL_SNAP_Y = 0.088;
/** Local Y of a jumper end when parked on a rest-post cylinder. */
const REST_SNAP_Y = 0.03;
/** Preallocated sag-curve vertices (Quest-cheap; no TubeGeometry rebuilds). */
const CABLE_SEGMENTS = 7;
const CABLE_POINT_COUNT = CABLE_SEGMENTS + 1;

interface JumperEndRecord {
  jumperId: string;
  end: "a" | "b";
  grabbable: Grabbable;
  restTarget: PlacementTarget;
}

interface CableVisual {
  line: THREE.Line;
  positions: Float32Array;
  endA: THREE.Object3D;
  endB: THREE.Object3D;
  parent: THREE.Object3D;
}

/** Dual-ended jumper mesh hooks (series/parallel bench or room panel). */
export interface JumperSpec {
  id: string;
  group: THREE.Group;
  endA: THREE.Group;
  endB: THREE.Group;
  cable: THREE.Object3D;
}

/** Seat one jumper end on a named circuit node after boot / restore. */
export interface JumperInitialSeat {
  jumperId: string;
  end: "a" | "b";
  nodeId: string;
}

/**
 * Generic jumper layout — rest posts, terminals, and optional initial seating.
 * Room panel and Series/Parallel both feed this; GrabManager stays shared.
 */
export interface JumperLayout {
  /** Ancestor that contains rest posts named `jumper-rest-{n}-{a|b}`. */
  restRoot: THREE.Object3D;
  terminals: Record<string, THREE.Group>;
  jumpers: readonly JumperSpec[];
  /**
   * Max plugs per circuit terminal. If omitted, uses `userData.maxOccupants`
   * when set, otherwise 2 (series/parallel rails).
   */
  terminalMaxOccupants?: (nodeId: string) => number;
  /** Accept a node as a circuit connection endpoint. */
  isCircuitNodeId?: (id: string) => boolean;
  /**
   * After boot / {@link JumperSystem.restoreInitial}, seat these ends on
   * terminals. All other ends park on rest posts.
   */
  initialSeating?: readonly JumperInitialSeat[];
}

export interface JumperSystemOptions {
  rig: XRRig;
  interaction: InteractionSystem;
  /** Preferred: panel or bench described as a layout. */
  layout?: JumperLayout;
  /** Series & Parallel bench — converted to {@link layout}. */
  bench?: SeriesParallelBenchRoots;
  /** Fired after snap / unplug / restore changes occupancy. */
  onConnectionsChanged?: () => void;
}

/**
 * Dual-ended jumper grab/snap for a circuit panel or bench.
 *
 * Shared {@link GrabManager} pipeline (same as outlet wires):
 * - Each endpoint is independently grabbable (left/right, no double ownership).
 * - Snap to circuit terminals or rest posts (`accepts: jumper-end`).
 * - Rest posts: one occupant. Circuit terminals: occupancy from layout
 *   (room `srcP` = 5 so all outputs can be jumpered; outputs = 1).
 * - A plug over capacity, or both ends of the same jumper on one terminal, reject.
 * - Non-target drops stay unlocked in world.
 * - Seated ends unplug on grab (unlock + hold). Outlet wires keep
 *   `unplugOnGrab: false` in their own system.
 * - Session force-drop does not snap and does not wipe seated jumpers.
 *
 * Hand tracking / pinch is deferred — controller squeeze + desktop click-to-pick
 * / click-to-place cover this station.
 */
export class JumperSystem {
  private readonly layout: JumperLayout;
  private readonly grabs: GrabManager;
  private readonly onConnectionsChanged?: () => void;

  private readonly ends: JumperEndRecord[] = [];
  private readonly cables: CableVisual[] = [];
  private readonly restHitHelpers: THREE.Mesh[] = [];
  private readonly terminalTargets = new Map<string, PlacementTarget>();
  private readonly initialByEnd = new Map<string, string>();

  private readonly _a = new THREE.Vector3();
  private readonly _b = new THREE.Vector3();
  private readonly _mid = new THREE.Vector3();

  constructor(opts: JumperSystemOptions) {
    const layout = opts.layout ?? (opts.bench ? layoutFromSeriesParallelBench(opts.bench) : null);
    if (!layout) {
      throw new Error("[JumperSystem] layout or bench is required");
    }
    this.layout = layout;
    this.onConnectionsChanged = opts.onConnectionsChanged;
    for (const seat of layout.initialSeating ?? []) {
      this.initialByEnd.set(`${seat.jumperId}:${seat.end}`, seat.nodeId);
    }
    this.grabs = new GrabManager({
      rig: opts.rig,
      interaction: opts.interaction,
      onPlaceOutcome: (o) => this.onPlaceOutcome(o),
    });

    this.registerTargetsAndEnds();
    this.buildCableVisuals();
    this.seatInitialLayout();
    this.updateCables();
  }

  update(dt: number): void {
    this.grabs.update(dt);
    this.updateCables();
  }

  /**
   * Force-drop held plugs without snap. Seated (locked) ends stay put.
   * Session start/end must not leave phantom ownership.
   */
  releaseSessionHolds(): void {
    this.grabs.reset();
    this.updateCables();
  }

  /** Park every end on its rest post (open circuit). Station-local reset. */
  restoreToRests(): void {
    this.grabs.restoreAll();
    this.seatAllOnRestPosts();
    this.updateCables();
    this.onConnectionsChanged?.();
  }

  /**
   * Restore the seating map last passed to the constructor or
   * {@link restoreSeating}. Session holds are dropped first; already-seated
   * ends are re-locked. Unlisted ends park on rest posts.
   */
  restoreInitial(): void {
    this.grabs.restoreAll();
    this.seatInitialLayout();
    this.updateCables();
    this.onConnectionsChanged?.();
  }

  /**
   * Replace the restore seating map and immediately seat it. Used by the
   * room panel to match the active fault (healthy jumpers minus the open).
   */
  restoreSeating(seats: readonly JumperInitialSeat[]): void {
    this.initialByEnd.clear();
    for (const seat of seats) {
      this.initialByEnd.set(`${seat.jumperId}:${seat.end}`, seat.nodeId);
    }
    this.restoreInitial();
  }

  /**
   * Jumper leads whose *both* ends are locked on circuit terminals.
   * Rest-post parking is ignored. Self-loops are omitted.
   */
  collectConnections(): Array<[string, string]> {
    const byJumper = new Map<string, { a?: string; b?: string }>();

    for (const rec of this.ends) {
      const target = this.occupiedTarget(rec.grabbable);
      if (!target) continue;
      const node = this.circuitNodeId(target);
      if (!node) continue;
      let pair = byJumper.get(rec.jumperId);
      if (!pair) {
        pair = {};
        byJumper.set(rec.jumperId, pair);
      }
      if (rec.end === "a") pair.a = node;
      else pair.b = node;
    }

    const connections: Array<[string, string]> = [];
    for (const pair of byJumper.values()) {
      if (!pair.a || !pair.b) continue;
      if (pair.a === pair.b) continue;
      connections.push([pair.a, pair.b]);
    }
    return connections;
  }

  dispose(): void {
    this.grabs.dispose();
    for (const helper of this.restHitHelpers) {
      helper.removeFromParent();
      helper.geometry.dispose();
      const mat = helper.material;
      if (mat instanceof THREE.Material) mat.dispose();
    }
    this.restHitHelpers.length = 0;
    for (const cable of this.cables) {
      cable.line.removeFromParent();
      cable.line.geometry.dispose();
      const mat = cable.line.material;
      if (mat instanceof THREE.Material) mat.dispose();
    }
    this.cables.length = 0;
  }

  private registerTargetsAndEnds(): void {
    const snapQuat = new THREE.Quaternion();
    const terminalSnap = new THREE.Vector3(0, TERMINAL_SNAP_Y, 0);
    const restSnap = new THREE.Vector3(0, REST_SNAP_Y, 0);

    const restByKey = new Map<string, PlacementTarget>();

    for (const jumper of this.layout.jumpers) {
      for (const end of ["a", "b"] as const) {
        const name = restPostName(jumper.id, end);
        const post = this.layout.restRoot.getObjectByName(name);
        if (!post) {
          console.warn(`[JumperSystem] missing ${name}`);
          continue;
        }
        this.addRestHitHelper(post);
        uniquifyStandardMaterials(post);
        const target = createPlacementTarget({
          id: name,
          object: post,
          accepts: "jumper-end",
          snapRadius: DEFAULT_WIRE_SNAP_RADIUS,
          snapLocalPosition: restSnap,
          snapLocalQuaternion: snapQuat,
        });
        this.grabs.addTarget(target);
        restByKey.set(`${jumper.id}:${end}`, target);
      }
    }

    for (const [nodeId, terminal] of Object.entries(this.layout.terminals)) {
      uniquifyStandardMaterials(terminal);
      const target = createPlacementTarget({
        id: `terminal-${nodeId}`,
        object: terminal,
        accepts: "jumper-end",
        snapRadius: DEFAULT_WIRE_SNAP_RADIUS,
        snapLocalPosition: terminalSnap,
        snapLocalQuaternion: snapQuat,
        maxOccupants: this.maxOccupantsFor(nodeId, terminal),
      });
      this.grabs.addTarget(target);
      this.terminalTargets.set(nodeId, target);
    }

    for (const jumper of this.layout.jumpers) {
      const recA = this.registerEnd(jumper.id, "a", jumper.endA, restByKey);
      const recB = this.registerEnd(jumper.id, "b", jumper.endB, restByKey);
      if (recA) this.ends.push(recA);
      if (recB) this.ends.push(recB);
    }
  }

  private maxOccupantsFor(nodeId: string, terminal: THREE.Object3D): number {
    if (this.layout.terminalMaxOccupants) {
      return Math.max(1, this.layout.terminalMaxOccupants(nodeId));
    }
    const fromData = terminal.userData.maxOccupants;
    if (typeof fromData === "number" && fromData >= 1) return fromData;
    return 2;
  }

  private registerEnd(
    jumperId: string,
    end: "a" | "b",
    object: THREE.Group,
    restByKey: Map<string, PlacementTarget>,
  ): JumperEndRecord | null {
    const restTarget = restByKey.get(`${jumperId}:${end}`);
    if (!restTarget) {
      console.warn(`[JumperSystem] missing rest target for ${jumperId} ${end}`);
      return null;
    }

    const tip =
      object.getObjectByName(`${jumperId}-end-${end}-tip`) ?? object;

    uniquifyStandardMaterials(object);
    const grabbable = createGrabbable({
      id: `${jumperId}-end-${end}`,
      object,
      placeAnchor: tip,
      unplugOnGrab: true,
    });
    this.grabs.addGrabbable(grabbable);

    const rec: JumperEndRecord = {
      jumperId,
      end,
      grabbable,
      restTarget,
    };
    return rec;
  }

  private addRestHitHelper(post: THREE.Object3D): void {
    const hit = new THREE.Mesh(
      new THREE.SphereGeometry(0.036, 8, 8),
      new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    );
    hit.name = `${post.name}-hit`;
    post.add(hit);
    this.restHitHelpers.push(hit);
  }

  private buildCableVisuals(): void {
    for (const jumper of this.layout.jumpers) {
      jumper.cable.visible = false;

      const positions = new Float32Array(CABLE_POINT_COUNT * 3);
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute(
        "position",
        new THREE.BufferAttribute(positions, 3),
      );
      geometry.setDrawRange(0, CABLE_POINT_COUNT);
      geometry.computeBoundingSphere();

      const color = cableColor(jumper.cable);
      const line = new THREE.Line(
        geometry,
        new THREE.LineBasicMaterial({
          color,
          depthWrite: false,
        }),
      );
      line.name = `${jumper.id}-cable-line`;
      line.frustumCulled = false;
      line.userData = {
        kind: "jumper-cable-line",
        jumperId: jumper.id,
      };
      jumper.group.add(line);

      this.cables.push({
        line,
        positions,
        endA: jumper.endA,
        endB: jumper.endB,
        parent: jumper.group,
      });
    }
  }

  private seatAllOnRestPosts(): void {
    for (const rec of this.ends) {
      lockIntoTarget(rec.grabbable, rec.restTarget);
    }
  }

  private seatInitialLayout(): void {
    if (this.initialByEnd.size === 0) {
      this.seatAllOnRestPosts();
      return;
    }
    for (const rec of this.ends) {
      const nodeId = this.initialByEnd.get(`${rec.jumperId}:${rec.end}`);
      if (nodeId) {
        const target = this.terminalTargets.get(nodeId);
        if (target) {
          lockIntoTarget(rec.grabbable, target);
          continue;
        }
        console.warn(
          `[JumperSystem] missing terminal ${nodeId} for ${rec.jumperId} ${rec.end}`,
        );
      }
      lockIntoTarget(rec.grabbable, rec.restTarget);
    }
  }

  private occupiedTarget(grabbable: Grabbable): PlacementTarget | null {
    for (const t of this.grabs.getTargets()) {
      if (isOccupiedBy(t, grabbable)) return t;
    }
    return null;
  }

  private circuitNodeId(target: PlacementTarget): string | null {
    const id = target.object.userData.nodeId;
    if (typeof id !== "string") return null;
    const check = this.layout.isCircuitNodeId;
    if (check && !check(id)) return null;
    return id;
  }

  private onPlaceOutcome(outcome: PlaceOutcome): void {
    if (outcome.kind === "rejected") return;
    if (
      outcome.kind === "locked" ||
      outcome.kind === "unplugged" ||
      outcome.kind === "dropped"
    ) {
      this.onConnectionsChanged?.();
    }
  }

  private updateCables(): void {
    for (const cable of this.cables) {
      cable.endA.getWorldPosition(this._a);
      cable.endB.getWorldPosition(this._b);
      cable.parent.worldToLocal(this._a);
      cable.parent.worldToLocal(this._b);

      this._mid.addVectors(this._a, this._b).multiplyScalar(0.5);
      const dx = this._a.x - this._b.x;
      const dy = this._a.y - this._b.y;
      const dz = this._a.z - this._b.z;
      const span = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const sag = Math.min(0.08, 0.028 + span * 0.18);
      this._mid.y += sag;

      const pos = cable.positions;
      for (let i = 0; i < CABLE_POINT_COUNT; i++) {
        const t = i / CABLE_SEGMENTS;
        const mt = 1 - t;
        // Quadratic Bezier through sag midpoint — write in place, no alloc.
        const x =
          mt * mt * this._a.x + 2 * mt * t * this._mid.x + t * t * this._b.x;
        const y =
          mt * mt * this._a.y + 2 * mt * t * this._mid.y + t * t * this._b.y;
        const z =
          mt * mt * this._a.z + 2 * mt * t * this._mid.z + t * t * this._b.z;
        const o = i * 3;
        pos[o] = x;
        pos[o + 1] = y;
        pos[o + 2] = z;
      }
      const attr = cable.line.geometry.getAttribute("position");
      attr.needsUpdate = true;
    }
  }
}

export function layoutFromSeriesParallelBench(
  bench: SeriesParallelBenchRoots,
): JumperLayout {
  return {
    restRoot: bench.workbench,
    terminals: bench.terminals,
    jumpers: bench.jumpers,
    terminalMaxOccupants: () => 2,
    isCircuitNodeId: isSeriesParallelNodeId,
  };
}

function restPostName(jumperId: string, end: "a" | "b"): string {
  const idx = jumperId.replace(/^jumper-/, "");
  return `jumper-rest-${idx}-${end}`;
}

/**
 * Clone standard materials so grab hover / lock pulses stay on this object.
 * Room kit shares copper/steel across the garage; pulsing the shared mat would
 * flash the fan, outlet plate, and every terminal at once.
 */
function uniquifyStandardMaterials(object: THREE.Object3D): void {
  object.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    const next = mats.map((m) =>
      m instanceof THREE.MeshStandardMaterial ? m.clone() : m,
    );
    mesh.material = Array.isArray(mesh.material) ? next : next[0]!;
  });
}

function cableColor(cable: THREE.Object3D): number {
  const mesh = cable as THREE.Mesh;
  const mat = mesh.material;
  if (mat instanceof THREE.MeshStandardMaterial) return mat.color.getHex();
  if (mat instanceof THREE.MeshBasicMaterial) return mat.color.getHex();
  return 0x444444;
}
